import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useAccessibleSubmit from "@tCentro/Common/Utilities/Hooks/useAccessibleSubmit";
import cenQtyMultiCheckoutErr from "@tCentro/Handlers/CENQtyMultiCheckoutErrHandler";
import CENCartTrash from "@tCentro/Icons/CENCartTrash";
import CENErrorIcon from "@tCentro/Icons/CENErrorIcon";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    setIsDelete?: (value: boolean) => void;
}

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
    qtyMinMultiErr: state.t_components.isMinOrMultiError.isMinErrorOrMultiError,
});

const mapDispatchToProps = {
    removeCartLine,
    cenQtyMultiCheckoutErr,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
    quantity?: InjectableCss;
    parentContainer?: InjectableCss;
    parentParentContainer?: InjectableCss;
    subQTYBtn?: ButtonProps;
    errorMsgStyle?: InjectableCss;
    errorIconStyles?: IconPresentationProps;
    errorMsgStyleTypo?: TypographyPresentationProps;

    addQTYBtn?: ButtonProps;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    parentParentContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            max-width: 124px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        max-width: 155px;
                    `,
                    null,
                    css`
                        position: relative;
                    `,
                ])}
        `,
    },
    errorMsgStyleTypo: {
        color: "danger",
        css: css`
            font-weight: 400;
            font-size: 12px;
            line-height: 16px;
        `,
    },
    errorIconStyles: {
        size: 16,
        css: css`
            span {
                width: 16px;
                height: 16px;
            }
        `,
    },
    errorMsgStyle: {
        css: css`
            display: flex;
            margin-top: 8px;
            justify-content: flex-start;
            gap: 4px;
            align-items: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-width: 250px;
                    `,
                    css`
                        min-width: 250px;
                    `,
                    css`
                        min-width: max-content;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    addQTYBtn: {
        css: css`
            height: 16px;
            min-height: 16px !important;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            right: 20px;
            margin-bottom: 4px;
            z-index: 1;
            border: none;
            & svg {
                color: ${COLORS.black};
                width: 16px;
                height: 16px;
            }
            & span {
                width: 16px;
                height: 16px;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background: #e9e9e9;
            }
            &:active {
                background: transparent;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        right: 8px;
                    `,
                    css`
                        right: 8px;
                    `,
                    css`
                        right: 15px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    subQTYBtn: {
        css: css`
            height: 16px;
            width: 16px;
            min-height: 16px !important;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 20px;
            margin-bottom: 4px;
            z-index: 1;
            border: none;
            & svg {
                color: ${COLORS.black};
                width: 16px;
                height: 16px;
            }
            & span {
                width: 16px;
                height: 16px;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background-color: #e9e9e9;
            }
            &:active {
                background: transparent;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        left: 38px;
                    `,
                    css`
                        left: 38px;
                    `,
                    css`
                        left: 42px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: 10px;
                text-transform: uppercase;
                font-weight: 600;
            `,
        },
    },
    quantity: {
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.base};
            display: none;
            color: ${getProp("theme.typography.p.color")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: inherit;
                        display: block;
                        line-height: 16px;
                    `,
                    css`
                        width: inherit;
                        display: block;
                        line-height: 16px;
                    `,
                    css`
                        min-width: 30px;
                        display: block;
                        line-height: 16px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    parentContainer: {
        css: css`
            display: flex;
            width: 100%;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        position: relative;
                    `,
                    css`
                        position: relative;
                    `,
                    css`
                        position: relative;
                    `,
                    css`
                        position: relative;
                    `,
                    null,
                ])}
        `,
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    removeCartLine,
    cenQtyMultiCheckoutErr,
    setIsDelete,
    qtyMinMultiErr,
}) => {
    const multipleSaleQty = cartLine.properties?.multipleSaleQty;

    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            onError?.();
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const roundingRule = cartLine.properties?.roundingRule;
    const canAddMulti = !!(multipleSaleQty && Number(multipleSaleQty) > 0 && roundingRule === "Multiple Only");
    const { value, changeHandler, keyDownHandler, blurHandler, inc, dec } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
        canAddMulti,
        Number(multipleSaleQty),
    );
    React.useEffect(() => {
        const isInvalidMultiple =
            canAddMulti &&
            Number(value) > Number(multipleSaleQty) &&
            Number(value) % Number(multipleSaleQty || 1) !== 0;
        const isInvalidMin = canAddMulti && Number(value) < Number(multipleSaleQty);

        const isMinErrorOrMultiError = isInvalidMin || isInvalidMultiple;

        if (cenQtyMultiCheckoutErr) {
            cenQtyMultiCheckoutErr({ isMinErrorOrMultiError });
        }
    }, [value, canAddMulti, multipleSaleQty]);
    const isInvalidMultiple =
        canAddMulti && Number(value) > Number(multipleSaleQty) && Number(value) % Number(multipleSaleQty || 1) !== 0;
    const isInvalidMin = canAddMulti && Number(value) < Number(multipleSaleQty);

    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));
    const hasQtyError = isInvalidMultiple || isInvalidMin;
    const shouldShowTrashIcon =
        Number(value) === 1 || Number(value) === 0 || (canAddMulti && Number(value) === Number(multipleSaleQty));

    const inputRef = React.useRef<HTMLInputElement>(null);

    if (editable && !isOutOfStock(cartLine)) {
        return (
            <StyledWrapper {...styles.parentParentContainer}>
                <StyledWrapper {...styles.parentContainer}>
                    <Typography as="span" {...styles.quantity}>
                        QTY:
                    </Typography>
                    <Button
                        {...styles.subQTYBtn}
                        onMouseDown={e => {
                            e.preventDefault();
                            inputRef.current?.focus();
                            if (shouldShowTrashIcon) {
                                setIsDelete && setIsDelete(true);
                                !qtyMinMultiErr && removeCartLine({ cartLineId: cartLine.id });
                                cenQtyMultiCheckoutErr({ isMinErrorOrMultiError: false });
                            } else {
                                dec();
                            }
                        }}
                    >
                        <ButtonIcon src={shouldShowTrashIcon ? CENCartTrash : Minus} />
                    </Button>

                    <TextField
                        {...styles.editableQuantityTextField}
                        ref={inputRef}
                        type="number"
                        min={0}
                        max={999999}
                        value={value}
                        disabled={
                            !cart.canModifyOrder ||
                            cartLine.isPromotionItem ||
                            cart.type === "Job" ||
                            isLoading[cart.id]
                        }
                        onChange={changeHandler}
                        onKeyDown={keyDownHandler}
                        onBlur={blurHandler}
                        onClick={event => event.currentTarget.focus()}
                        data-test-selector="cartline_qty"
                        css={
                            hasQtyError
                                ? css`
                                      border: 2px solid ${COLORS.alertMsgRed} !important;
                                  `
                                : undefined
                        }
                    />
                    <Button
                        {...styles.addQTYBtn}
                        data-test-selector="addQTYBtn"
                        onMouseDown={e => {
                            e.preventDefault();
                            inputRef.current?.focus();
                            inc();
                        }}
                    >
                        <ButtonIcon src={Plus} />
                    </Button>
                </StyledWrapper>

                {isInvalidMultiple && (
                    <StyledWrapper {...styles.errorMsgStyle}>
                        <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                        <Typography {...styles.errorMsgStyleTypo}>
                            {translate(
                                `This product must be ordered in multiples of ${cartLine?.properties?.multipleSaleQty}`,
                            )}
                        </Typography>
                    </StyledWrapper>
                )}
                {isInvalidMin && (
                    <StyledWrapper {...styles.errorMsgStyle}>
                        <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                        <Typography {...styles.errorMsgStyleTypo}>
                            {translate(
                                `This product has a minimum order of ${cartLine?.properties?.multipleSaleQty} units`,
                            )}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }

    return (
        <SmallHeadingAndText
            {...styles.readOnlyQuantityText}
            heading={label || translate("QTY")}
            text={cartLine.qtyOrdered!}
            data-test-selector="quoteDetail_cardLine_qty"
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineQuantity));
